import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Register() {
  const { registerPhone } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);

  const validBase = () => {
    if (!name.trim()) { toast.error("الاسم مطلوب"); return false; }
    if (phone.replace(/[^\d+]/g, "").length < 8) { toast.error("رقم هاتف غير صالح"); return false; }
    if (password.length < 6) { toast.error("كلمة المرور 6 أحرف على الأقل"); return false; }
    return true;
  };

  const doRegister = async () => {
    setBusy(true);
    try {
      await registerPhone(name.trim(), phone.replace(/[^\d+]/g, ""), password, email.trim() || undefined);
      navigate("/");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const sendOtp = async () => {
    if (!validBase()) return;
    const em = email.trim().toLowerCase();
    if (!em || !em.includes("@")) return toast.error("أدخل بريدًا إلكترونيًا صالحًا");
    setOtpBusy(true);
    try {
      await api("/auth/otp/request", { method: "POST", body: JSON.stringify({ email: em }) });
      setOtpSent(true);
      toast.success("أرسلنا رمز تحقق إلى بريدك");
    } catch (e) { toast.error(e.message || "تعذّر إرسال الرمز"); }
    finally { setOtpBusy(false); }
  };

  const verifyOtp = async () => {
    if (otpCode.trim().length < 4) return toast.error("أدخل الرمز المكوّن من 6 أرقام");
    setOtpBusy(true);
    try {
      await api("/auth/otp/verify", { method: "POST", body: JSON.stringify({ email: email.trim().toLowerCase(), code: otpCode.trim() }) });
      setEmailVerified(true);
      toast.success("تم تأكيد بريدك ✅");
      await doRegister();
    } catch (e) { toast.error(e.message || "رمز غير صحيح"); }
    finally { setOtpBusy(false); }
  };

  const onSubmit = async () => {
    if (!validBase()) return;
    // Email is optional; if provided it must be verified via OTP before creating the account.
    if (email.trim() && !emailVerified) {
      return sendOtp();
    }
    await doRegister();
  };

  return (
    <div className="min-h-screen bg-[#FAFBFF] flex items-center justify-center px-4 py-10" dir="rtl">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="text-center mb-7">
          <div className="text-4xl font-black text-[#FF3D5A]">🛍️ نطاق للتسوق</div>
          <h1 className="text-2xl font-extrabold text-[#161629] mt-3">أنشئ حسابك</h1>
          <p className="text-[#6B7280] mt-1">وابدأ البيع أو الشراء</p>
        </div>
        <div className="bg-white rounded-3xl border border-[#E8EAF2] p-6 brand-shadow-sm">
          <label className="block text-sm font-bold text-[#161629] mb-1.5">الاسم *</label>
          <input data-testid="register-name-input" className="w-full bg-[#F0F2FA] rounded-2xl px-4 py-3.5 outline-none focus:ring-2 focus:ring-[#FF3D5A]" placeholder="اسمك الكامل" value={name} onChange={(e) => setName(e.target.value)} />

          <label className="block text-sm font-bold text-[#161629] mb-1.5 mt-3">رقم الهاتف *</label>
          <div className="flex items-center gap-2">
            <div className="h-[52px] rounded-2xl px-3 flex items-center bg-[#FFE5EA] border border-[#FF3D5A] text-[#FF3D5A] font-black text-sm shrink-0">🇱🇾 +218</div>
            <input data-testid="register-phone-input" className="flex-1 bg-[#F0F2FA] rounded-2xl px-4 py-3.5 outline-none focus:ring-2 focus:ring-[#FF3D5A]" placeholder="91XXXXXXX" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <p className="text-[11px] text-[#6B7280] mt-1">يمكنك الدخول لاحقاً برقم الهاتف</p>

          <label className="block text-sm font-bold text-[#161629] mb-1.5 mt-3">كلمة المرور *</label>
          <input data-testid="register-password-input" type="password" className="w-full bg-[#F0F2FA] rounded-2xl px-4 py-3.5 outline-none focus:ring-2 focus:ring-[#FF3D5A]" placeholder="6 أحرف على الأقل" value={password} onChange={(e) => setPassword(e.target.value)} />

          {!showEmail ? (
            <button data-testid="add-email-toggle" onClick={() => setShowEmail(true)} className="flex items-center gap-2 mt-4 px-3 py-2 rounded-xl bg-[#FFE5EA] text-[#FF3D5A] font-extrabold text-sm">
              <Mail size={18} /> إضافة بريد إلكتروني (اختياري)
            </button>
          ) : (
            <>
              <div className="flex items-center justify-between mt-3">
                <label className="text-sm font-bold text-[#161629]">البريد الإلكتروني (اختياري)</label>
                <button data-testid="remove-email" onClick={() => { setShowEmail(false); setEmail(""); }}><XCircle size={20} className="text-[#6B7280]" /></button>
              </div>
              <input data-testid="register-email-input" className="w-full bg-[#F0F2FA] rounded-2xl px-4 py-3.5 mt-1.5 outline-none focus:ring-2 focus:ring-[#FF3D5A]" placeholder="name@example.com" value={email} onChange={(e) => { setEmail(e.target.value); setOtpSent(false); setEmailVerified(false); setOtpCode(""); }} disabled={emailVerified} />
              {emailVerified && <p className="flex items-center gap-1.5 text-[#00D26A] text-sm font-bold mt-2"><ShieldCheck size={16} /> تم تأكيد البريد</p>}
              {otpSent && !emailVerified && (
                <div className="mt-3 p-3 rounded-2xl bg-[#FFF7E0] border border-[#FFB627]">
                  <label className="text-sm font-bold text-[#161629]">أدخل رمز التحقق المُرسَل إلى بريدك</label>
                  <input data-testid="register-otp-input" inputMode="numeric" maxLength={6} className="w-full bg-white rounded-xl px-4 py-3 mt-1.5 text-center tracking-[8px] font-black text-lg outline-none focus:ring-2 focus:ring-[#FF3D5A]" placeholder="------" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/[^\d]/g, ""))} />
                  <div className="flex gap-2 mt-2">
                    <button data-testid="register-otp-verify" disabled={otpBusy || busy} onClick={verifyOtp} className="flex-1 bg-[#00D26A] text-white font-black py-2.5 rounded-xl disabled:opacity-60">{otpBusy || busy ? "..." : "تأكيد الرمز وإنشاء الحساب"}</button>
                    <button data-testid="register-otp-resend" disabled={otpBusy} onClick={sendOtp} className="px-3 bg-white border border-[#E8EAF2] text-[#161629] font-bold py-2.5 rounded-xl text-sm">إعادة الإرسال</button>
                  </div>
                </div>
              )}
            </>
          )}

          {!(showEmail && email.trim() && otpSent && !emailVerified) && (
            <button data-testid="register-submit-button" disabled={busy || otpBusy} onClick={onSubmit} className="w-full bg-[#FF3D5A] text-white font-extrabold text-lg py-4 rounded-2xl mt-6 hover:bg-[#FF2548] transition-colors disabled:opacity-60">
              {busy || otpBusy ? "..." : (showEmail && email.trim() && !emailVerified ? "إرسال رمز تأكيد البريد" : "إنشاء الحساب")}
            </button>
          )}

          <Link to="/login" data-testid="go-to-login" className="block text-center text-[#6B7280] text-sm mt-5">
            لديك حساب؟ <span className="text-[#FF3D5A] font-extrabold">سجّل دخولك</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
